import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { request } from "app/network";
import { useMaterialUIController } from "context";

interface AddEditCommonCodeModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    commonCode: any | null;
    fixedGroupCode?: string;
}

interface AddEditCommonCodeModalState {
    groupCode: string;
    groupCodes: Array<{ code: string; name: string }>;
    code: string;
    name: string;
    description: string;
    sortOrder: number;
    extraValue1: string;
    extraValue2: string;
    extraValue3: string;
    isActive: boolean;
    submitting: boolean;
}

class AddEditCommonCodeModalClass extends Component<
    AddEditCommonCodeModalProps & { controller: any },
    AddEditCommonCodeModalState
> {
    constructor(props: AddEditCommonCodeModalProps & { controller: any }) {
        super(props);

        const { commonCode, fixedGroupCode } = props;

        this.state = {
            groupCode: commonCode?.groupCode || fixedGroupCode || "",
            groupCodes: [],
            code: commonCode?.code || "",
            name: commonCode?.name || "",
            description: commonCode?.description || "",
            sortOrder: commonCode?.sortOrder || 0,
            extraValue1: commonCode?.extraValue1 || "",
            extraValue2: commonCode?.extraValue2 || "",
            extraValue3: commonCode?.extraValue3 || "",
            isActive: commonCode?.isActive ?? true,
            submitting: false,
        };
    }

    async componentDidMount() {
        await this.loadGroupCodes();
    }

    loadGroupCodes = async () => {
        try {
            const res = await request("CommonCode.getGroups");
            if (res && res.data) {
                const uniqueGroups = res.data
                    .map((group: any) => ({
                        code: group.groupCode,
                        name: group.groupName
                    }))
                    .sort((a: any, b: any) => a.code.localeCompare(b.code));

                this.setState({ groupCodes: uniqueGroups });
            }
        } catch (error: any) {
            console.error("그룹코드 목록 조회 실패:", error);
        }
    };


    handleSubmit = async () => {
        const { onSuccess, onClose, commonCode } = this.props;
        const {
            groupCode,
            code,
            name,
            description,
            sortOrder,
            extraValue1,
            extraValue2,
            extraValue3,
            isActive,
        } = this.state;

        // Validation
        if (!groupCode.trim()) {
            alert("그룹코드를 입력해주세요.");
            return;
        }
        if (!code.trim()) {
            alert("코드를 입력해주세요.");
            return;
        }
        if (!name.trim()) {
            alert("이름을 입력해주세요.");
            return;
        }

        try {
            this.setState({ submitting: true });

            const param = {
                GroupCode: groupCode.trim(),
                Code: code.trim(),
                Name: name.trim(),
                Description: description.trim() || null,
                SortOrder: sortOrder,
                ExtraValue1: extraValue1.trim() || null,
                ExtraValue2: extraValue2.trim() || null,
                ExtraValue3: extraValue3.trim() || null,
                IsActive: isActive,
            };

            if (commonCode) {
                // Update
                await request("CommonCode.update", {
                    paramz: { ...param, SeqId: commonCode.seqId },
                    pathParams: { seqId: commonCode.seqId.toString() },
                });
                alert("공통코드가 수정되었습니다.");
            } else {
                // Create
                await request("CommonCode.create", { paramz: param });
                alert("공통코드가 등록되었습니다.");
            }

            onSuccess();
        } catch (error: any) {
            console.error("공통코드 처리 실패:", error);
            alert(error?.response?.data?.message || "공통코드 처리에 실패했습니다.");
        } finally {
            this.setState({ submitting: false });
        }
    };

    render() {
        const { open, onClose, commonCode, controller, fixedGroupCode } = this.props;
        const [darkMode] = controller;

        const {
            groupCode,
            groupCodes,
            code,
            name,
            description,
            sortOrder,
            extraValue1,
            extraValue2,
            extraValue3,
            isActive,
            submitting,
        } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>
                    <MDTypography variant="h5" fontWeight="medium">
                        {fixedGroupCode ? (commonCode ? "카테고리 수정 (공통코드)" : "카테고리 추가 (공통코드)") : (commonCode ? "공통코드 수정" : "공통코드 추가")}
                    </MDTypography>
                </DialogTitle>
                <DialogContent>
                    <MDBox pt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    options={groupCodes}
                                    value={groupCode}
                                    inputValue={groupCode}
                                    onChange={(_, newValue) => {
                                        if (newValue && typeof newValue === 'object') {
                                            this.setState({ groupCode: newValue.code });
                                        }
                                    }}
                                    onInputChange={(_, newValue) => {
                                        this.setState({ groupCode: newValue });
                                    }}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return `${option.code} - ${option.name}`;
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.code}>
                                            {option.code} - {option.name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="그룹코드 *"
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{
                                                ...params.InputLabelProps,
                                                sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                sx: {
                                                    color: darkMode ? "#fff" : "#000",
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                    disabled={!!commonCode || !!fixedGroupCode}
                                    size="small"
                                    sx={{
                                        "& .MuiAutocomplete-paper": {
                                            backgroundColor: darkMode ? "#1a2035" : "#fff",
                                        },
                                        "& .MuiAutocomplete-listbox": {
                                            backgroundColor: darkMode ? "#1a2035" : "#fff",
                                            "& .MuiAutocomplete-option": {
                                                color: darkMode ? "#fff" : "#000",
                                                "&:hover": {
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                },
                                            },
                                        },
                                    }}
                                    componentsProps={{
                                        paper: {
                                            sx: {
                                                backgroundColor: darkMode ? "#1a2035" : "#fff",
                                                color: darkMode ? "#fff" : "#000",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="코드 *"
                                    value={code}
                                    onChange={(e: any) => this.setState({ code: e.target.value })}
                                    fullWidth
                                    size="small"
                                    disabled={!!commonCode}
                                    InputLabelProps={{
                                        sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                    }}
                                    InputProps={{
                                        sx: {
                                            color: darkMode ? "#fff" : "#000",
                                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="이름 *"
                                    value={name}
                                    onChange={(e: any) => this.setState({ name: e.target.value })}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{
                                        sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                    }}
                                    InputProps={{
                                        sx: {
                                            color: darkMode ? "#fff" : "#000",
                                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="설명"
                                    value={description}
                                    onChange={(e: any) => this.setState({ description: e.target.value })}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    InputLabelProps={{
                                        sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                    }}
                                    InputProps={{
                                        sx: {
                                            color: darkMode ? "#fff" : "#000",
                                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            {!fixedGroupCode && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="정렬순서"
                                            type="number"
                                            value={sortOrder}
                                            onChange={(e: any) =>
                                                this.setState({ sortOrder: parseInt(e.target.value) || 0 })
                                            }
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{
                                                sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            InputProps={{
                                                sx: {
                                                    color: darkMode ? "#fff" : "#000",
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox display="flex" alignItems="center" height="100%">
                                            <MDTypography
                                                variant="button"
                                                fontWeight="regular"
                                                sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}
                                            >
                                                활성화
                                            </MDTypography>
                                            <Switch
                                                checked={isActive}
                                                onChange={(e) => this.setState({ isActive: e.target.checked })}
                                                color="primary"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="추가값 1"
                                            value={extraValue1}
                                            onChange={(e: any) => this.setState({ extraValue1: e.target.value })}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{
                                                sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            InputProps={{
                                                sx: {
                                                    color: darkMode ? "#fff" : "#000",
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="추가값 2"
                                            value={extraValue2}
                                            onChange={(e: any) => this.setState({ extraValue2: e.target.value })}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{
                                                sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            InputProps={{
                                                sx: {
                                                    color: darkMode ? "#fff" : "#000",
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="추가값 3"
                                            value={extraValue3}
                                            onChange={(e: any) => this.setState({ extraValue3: e.target.value })}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{
                                                sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            InputProps={{
                                                sx: {
                                                    color: darkMode ? "#fff" : "#000",
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fff",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} variant="outlined" color="dark" disabled={submitting}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSubmit} variant="gradient" color="info" disabled={submitting}>
                        {submitting ? "저장 중..." : "저장"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default function AddEditCommonCodeModal(props: AddEditCommonCodeModalProps) {
    const controller = useMaterialUIController();
    return <AddEditCommonCodeModalClass {...props} controller={controller} />;
}
